import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ogImage } from '../../lib/ogImage';
import { Instagram, Menu, X, ChevronRight, CalendarDays } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../../i18n';
import { useConsent } from '../../context/ConsentContext';
import AuthMenu from '../../components/auth/AuthMenu';
import logoImage80 from '../../assets/logo.jpg?w=80&format=jpg&imagetools';
import logoImage160 from '../../assets/logo.jpg?w=160&format=jpg&imagetools';
import logoImage240 from '../../assets/logo.jpg?w=240&format=jpg&imagetools';

const posts = [
  {
    slug: '/blog/first-tattoo-athens',
    titleEl: 'Το Πρώτο σου Τατουάζ στην Αθήνα',
    titleEn: 'Your First Tattoo in Athens',
    descEl: 'Κάνε το πρώτο σου τατουάζ με ασφάλεια και επαγγελματισμό. Custom σχέδια, εμπειρία από το 2018 και φιλική εξυπηρέτηση.',
    descEn: 'Get your first tattoo safely and professionally. Custom designs, since 2018 and friendly service.',
    date: '17 Μαρτίου 2026',
  },
  {
    slug: '/blog/tattoo-aftercare-athens',
    titleEl: 'Φροντίδα Τατουάζ Μετά το Studio',
    titleEn: 'Tattoo Aftercare After Your Session',
    descEl: 'Πλήρεις οδηγίες aftercare τατουάζ. Γρήγορη επούλωση, διατήρηση χρώματος και αποφυγή μολύνσεων.',
    descEn: 'Complete tattoo aftercare instructions. Fast healing, colour retention and infection prevention.',
    date: '25 Μαρτίου 2026',
  },
  {
    slug: '/blog/tattoo-care-athens',
    titleEl: 'Φροντίδα Τατουάζ στην Αθήνα',
    titleEn: 'Tattoo Care in Athens',
    descEl: 'Μάθετε πώς να φροντίζετε το τατουάζ σας για γρήγορη επούλωση και διατήρηση χρώματος μακροπρόθεσμα.',
    descEn: 'Learn how to care for your tattoo for fast healing and long-term colour preservation.',
    date: '1 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-styles-athens',
    titleEl: 'Στυλ Τατουάζ στην Αθήνα',
    titleEn: 'Tattoo Styles in Athens',
    descEl: 'Ανακαλύψτε όλα τα στυλ τατουάζ: realism, traditional, minimal, watercolor και geometric.',
    descEn: 'Discover all tattoo styles: realism, traditional, minimal, watercolour and geometric.',
    date: '9 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-trends-athens',
    titleEl: 'Τάσεις Τατουάζ στην Αθήνα 2026',
    titleEn: 'Tattoo Trends in Athens 2026',
    descEl: 'Κορυφαίες τάσεις τατουάζ στην Αθήνα: minimal, watercolor, black & grey realism και neo-traditional.',
    descEn: 'Top tattoo trends in Athens: minimal, watercolour, black & grey realism and neo-traditional.',
    date: '16 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-cost-athens',
    titleEl: 'Πόσο κοστίζει ένα τατουάζ στην Αθήνα;',
    titleEn: 'How Much Does a Tattoo Cost in Athens?',
    descEl: 'Μάθε τι επηρεάζει την τιμή ενός τατουάζ: μέγεθος, στυλ, εμπειρία artist και custom design. Ρεαλιστικός οδηγός κοστολόγησης.',
    descEn: 'Find out what affects tattoo pricing in Athens: size, style, artist experience and custom design.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-artist-athens',
    titleEl: 'Πώς να Διαλέξεις Tattoo Artist στην Αθήνα',
    titleEn: 'How to Choose a Tattoo Artist in Athens',
    descEl: 'Τι να προσέξεις πριν επιλέξεις studio: portfolio, υγιεινή, consultation και red flags που δεν πρέπει να αγνοείς.',
    descEn: 'What to look for before choosing a studio: portfolio, hygiene, consultation and red flags to watch out for.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/fine-line-tattoo-guide-athens',
    titleEl: 'Fine Line Tattoos στην Αθήνα: Οδηγός πριν κλείσεις',
    titleEn: 'Fine Line Tattoos in Athens: Guide Before You Book',
    descEl: 'Τι κάνει ένα fine line tattoo ποιοτικό, πώς γερνά, κοινά λάθη, placements και ερωτήσεις για το consultation.',
    descEn: 'What makes a fine line tattoo quality, how it ages, common mistakes, placements and consultation questions.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-cover-up-athens',
    titleEl: 'Tattoo Cover Up στην Αθήνα: Πότε Γίνεται και Πώς',
    titleEn: 'Tattoo Cover Up in Athens: When It Works and How',
    descEl: 'Πότε μπορεί να καλυφθεί ένα παλιό tattoo, τι κάνει ένα cover up πετυχημένο και πότε χρειάζεται fading πρώτα.',
    descEn: 'When an old tattoo can be covered, what makes a cover up successful and when fading is needed first.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-pain-guide-athens',
    titleEl: 'Πονάει το Τατουάζ; Τα Πιο Επώδυνα Σημεία',
    titleEn: 'Does a Tattoo Hurt? The Most Painful Spots',
    descEl: 'Πόσο πονάει πραγματικά ένα τατουάζ, ποια είναι τα πιο ευαίσθητα σημεία και τι να κάνεις πριν τη συνεδρία.',
    descEn: 'How much a tattoo really hurts, which spots are most sensitive and what to do before your session.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-placement-guide-athens',
    titleEl: 'Πού να Κάνεις Τατουάζ; Οδηγός για Placement',
    titleEn: 'Where to Get a Tattoo? A Guide to Placement',
    descEl: 'Πώς να διαλέξεις σημείο για το επόμενο tattoo σου: visibility, πόνος, επούλωση και πώς το σώμα επηρεάζει το design.',
    descEn: 'How to choose the right placement for your next tattoo: visibility, pain, healing and how the body affects the design.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/minimal-tattoo-ideas-athens',
    titleEl: 'Ιδέες για Minimal Τατουάζ με Νόημα',
    titleEn: 'Meaningful Minimal Tattoo Ideas',
    descEl: 'Minimal tattoo ιδέες που αντέχουν στον χρόνο: σύμβολα με νόημα, placements και η σημασία της ακρίβειας εκτέλεσης.',
    descEn: 'Minimal tattoo ideas that stand the test of time: meaningful symbols, placements and the importance of precise execution.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-style-guide-athens',
    titleEl: 'Blackwork, Fine Line ή Realism; Ποιο Στυλ να Διαλέξεις',
    titleEn: 'Blackwork, Fine Line or Realism? Which Style to Choose',
    descEl: 'Πώς να επιλέξεις το σωστό tattoo style: τι χαρακτηρίζει κάθε στυλ και πώς να βρεις αυτό που ταιριάζει στην αισθητική σου.',
    descEn: 'How to choose the right tattoo style: what defines each style and how to find the one that fits your aesthetic.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/first-tattoo-design-athens',
    titleEl: 'Πώς να Επιλέξεις Σχέδιο για Πρώτο Τατουάζ',
    titleEn: 'How to Choose a Design for Your First Tattoo',
    descEl: 'Ιδέες και συμβουλές για το πρώτο σου tattoo: προσωπικό νόημα, custom design και τα πιο συνηθισμένα λάθη που πρέπει να αποφύγεις.',
    descEn: 'Ideas and tips for your first tattoo: personal meaning, custom design and the most common mistakes to avoid.',
    date: '24 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-symbolism-guide-athens',
    titleEl: 'Συμβολισμός Τατουάζ: Οδηγός για Σχέδια με Νόημα',
    titleEn: 'Tattoo Symbolism: A Guide to Meaningful Designs',
    descEl: 'Ζώα, μυθολογικά, γεωμετρικά και πνευματικά σύμβολα: πώς να επιλέξεις σχέδιο με ουσιαστικό νόημα για ένα τατουάζ που αντέχει στον χρόνο.',
    descEn: 'Animals, mythological, geometric and spiritual symbols: how to choose a design with real meaning for a tattoo that stands the test of time.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/meaningful-tattoo-ideas-athens',
    titleEl: 'Ιδέες για Τατουάζ με Προσωπικό Νόημα',
    titleEn: 'Tattoo Ideas with Personal Meaning',
    descEl: 'Script, σύμβολα και σχέδια που αντιπροσωπεύουν την προσωπική σου ιστορία. Πώς να μετατρέψεις μια ιδέα σε τατουάζ που σημαίνει κάτι.',
    descEn: 'Script, symbols and designs that represent your personal story. How to turn an idea into a tattoo that means something.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/summer-tattoo-care-athens',
    titleEl: 'Τατουάζ το Καλοκαίρι: Ήλιος, Θάλασσα και Φροντίδα',
    titleEn: 'Summer Tattoo: Sun, Sea and Aftercare',
    descEl: 'Όλα όσα πρέπει να ξέρεις για tattoo το καλοκαίρι: ήλιος, θάλασσα, ιδρώτας και πώς να φροντίσεις σωστά ένα φρέσκο τατουάζ.',
    descEn: 'Everything you need to know about getting a tattoo in summer: sun, sea, sweat and how to properly care for a fresh tattoo.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/laser-vs-cover-up-athens',
    titleEl: 'Λέιζερ ή Cover Up; Τι να Επιλέξεις για Παλιό Τατουάζ',
    titleEn: 'Laser or Cover Up? What to Choose for an Old Tattoo',
    descEl: 'Πότε αξίζει λέιζερ αφαίρεση και πότε cover up. Ποιες οι διαφορές, η διαδικασία και πώς να πάρεις τη σωστή απόφαση.',
    descEn: 'When laser removal is worth it and when a cover up is better. The differences, the process and how to make the right decision.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/walk-in-vs-appointment-athens',
    titleEl: 'Walk-in ή Ραντεβού; Τι να Επιλέξεις για Tattoo στην Αθήνα',
    titleEn: 'Walk-in or Appointment? What to Choose for a Tattoo in Athens',
    descEl: 'Διαφορά walk-in και ραντεβού για τατουάζ: πότε να επιλέξεις το καθένα και πώς λειτουργεί στα studios της Αθήνας.',
    descEn: 'The difference between walk-in and appointment for a tattoo: when to choose each and how it works at Athens studios.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/best-time-for-tattoo-athens',
    titleEl: 'Ποια Εποχή είναι Καλύτερη για Τατουάζ;',
    titleEn: 'What is the Best Time of Year for a Tattoo?',
    descEl: 'Φθινόπωρο, χειμώνας, άνοιξη ή καλοκαίρι: ποια εποχή να κάνεις τατουάζ στην Αθήνα και ποια πρακτικά κριτήρια να λάβεις υπόψη.',
    descEn: 'Autumn, winter, spring or summer: the best time of year for a tattoo in Athens and the practical criteria to consider.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/fine-line-tattoo-athens',
    titleEl: 'Fine Line Τατουάζ: Τι Ορίζει το Στυλ και σε Ποιον Ταιριάζει',
    titleEn: 'Fine Line Tattoo: What Defines the Style and Who It Suits',
    descEl: 'Τι είναι το fine line ως αισθητικό κίνημα, ποια υποκατηγορία (single needle, botanical, geometric, script) ταιριάζει σε ποιον, και γιατί έγινε τόσο δημοφιλές.',
    descEn: 'What fine line is as an aesthetic movement, which sub-style suits who, and why it became so popular in Athens.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/custom-tattoo-process-athens',
    titleEl: 'Πώς Γίνεται ένα Custom Τατουάζ: Από την Ιδέα στο Σχέδιο',
    titleEn: 'How a Custom Tattoo is Made: From Idea to Design',
    descEl: 'Η πλήρης διαδικασία πίσω από ένα custom τατουάζ: consultation, σχεδιασμός, προσαρμογή στο σώμα και η ημέρα της συνεδρίας.',
    descEn: 'The full process behind a custom tattoo: consultation, design, body adaptation and the day of the session.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-difficult-placements-athens',
    titleEl: 'Τατουάζ σε Δάχτυλα, Λαιμό και Δύσκολα Σημεία: Τι Πρέπει να Ξέρεις',
    titleEn: 'Tattoos on Fingers, Neck and Difficult Spots: What You Need to Know',
    descEl: 'Δάχτυλα, λαιμός, αγκώνες και γόνατα: γιατί αυτά τα σημεία είναι απαιτητικά, πώς αλλάζει το design ανάλογα με την περιοχή και τι σημαίνει ρεαλιστική προσέγγιση.',
    descEn: 'Fingers, neck, elbows and knees: why these spots are demanding, how the design changes per area and what a realistic approach means.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/tattoo-timing-athens',
    titleEl: 'Πότε Είσαι Έτοιμος για Τατουάζ; Προσωπική Ετοιμότητα',
    titleEn: 'When Are You Ready for a Tattoo? Personal Readiness',
    descEl: 'Καριέρα, σχέσεις, σταθμοί ζωής και συναισθηματική ετοιμότητα. Τα ερωτήματα που καθορίζουν αν είσαι έτοιμος για νέο τατουάζ, πέρα από εποχές και aftercare.',
    descEn: 'Career, relationships, life milestones and emotional readiness. The questions that define whether you are ready for a new tattoo, beyond seasons and aftercare.',
    date: '25 Απριλίου 2026',
  },
  {
    slug: '/blog/small-tattoo-cost-athens',
    titleEl: 'Πόσο κοστίζει ένα μικρό τατουάζ στην Αθήνα;',
    titleEn: 'How Much Does a Small Tattoo Cost in Athens?',
    descEl: 'Όλοι οι παράγοντες που καθορίζουν την τιμή ενός μικρού τατουάζ: μέγεθος, λεπτομέρεια, χρώμα, σημείο σώματος και ελάχιστη χρέωση.',
    descEn: 'All the factors behind small tattoo pricing in Athens: size, detail, colour, body placement and minimum charge.',
    date: '29 Ιουνίου 2026',
  },
  {
    slug: '/blog/tattoo-touch-up-guide-athens',
    titleEl: 'Πότε χρειάζεται touch up ένα τατουάζ;',
    titleEn: 'When Does a Tattoo Need a Touch Up?',
    descEl: 'Γιατί ξεθωριάζει ένα τατουάζ με τον καιρό, πότε χρειάζεται ρετουσάρισμα και τι να περιμένεις από μια συνεδρία touch up.',
    descEn: 'Why tattoos fade over time, when a touch up is needed and what to expect from a touch up session.',
    date: '8 Ιουνίου 2026',
  },
  {
    slug: '/blog/sea-after-tattoo-athens',
    titleEl: 'Μπορώ να πάω θάλασσα μετά από τατουάζ;',
    titleEn: 'Can I Go to the Sea After Getting a Tattoo?',
    descEl: 'Πόσο πρέπει να περιμένεις πριν πας θάλασσα με φρέσκο τατουάζ και πώς να απολαύσεις το καλοκαίρι σου χωρίς ρίσκο.',
    descEn: 'How long to wait before going to the sea with a fresh tattoo and how to enjoy summer safely.',
    date: '15 Ιουνίου 2026',
  },
  {
    slug: '/blog/tattoo-healing-time-athens',
    titleEl: 'Πόσο χρόνο χρειάζεται να επουλωθεί ένα τατουάζ;',
    titleEn: 'How Long Does It Take for a Tattoo to Heal?',
    descEl: 'Όλα τα στάδια επούλωσης ενός τατουάζ και πώς να φροντίσεις σωστά το νέο σου τατουάζ.',
    descEn: 'All the healing stages of a tattoo and how to properly care for your new piece.',
    date: '22 Ιουνίου 2026',
  },
  {
    slug: '/blog/tattoo-design-mistakes-athens',
    titleEl: 'Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ',
    titleEn: 'The Most Common Mistakes When Choosing a Tattoo Design',
    descEl: 'Τα 10 πιο συχνά λάθη που κάνουν οι άνθρωποι όταν διαλέγουν σχέδιο τατουάζ και πώς να τα αποφύγεις.',
    descEn: 'The 10 most common mistakes people make when choosing a tattoo design and how to avoid them.',
    date: '1 Ιουνίου 2026',
  },
  {
    slug: '/blog/tattoo-on-scars-athens',
    titleEl: 'Μπορώ να κάνω τατουάζ πάνω σε ουλές;',
    titleEn: 'Can I Get a Tattoo Over Scars?',
    descEl: 'Πότε και πώς γίνεται το τατουάζ πάνω σε ουλές και γιατί η εμπειρία του καλλιτέχνη είναι κρίσιμη.',
    descEn: 'When and how tattoos over scars are done and why an experienced artist matters.',
    date: '5 Ιουνίου 2026',
  },
  {
    slug: '/blog/tattoo-infection-signs-athens',
    titleEl: 'Πώς καταλαβαίνεις ότι ένα τατουάζ έχει μολυνθεί;',
    titleEn: 'How Do You Know If a Tattoo Is Infected?',
    descEl: 'Πώς να ξεχωρίσεις τη φυσιολογική επούλωση από τη μόλυνση και τι να κάνεις σε κάθε περίπτωση.',
    descEn: 'How to tell normal healing apart from infection and what to do in each case.',
    date: '11 Ιουνίου 2026',
  },
  {
    slug: '/blog/what-to-eat-before-tattoo-athens',
    titleEl: 'Τι να φάω πριν από το τατουάζ;',
    titleEn: 'What Should You Eat Before Getting a Tattoo?',
    descEl: 'Τι να φας και τι να αποφύγεις πριν το ραντεβού σου για τατουάζ, για μια πιο ομαλή εμπειρία.',
    descEn: 'What to eat and what to avoid before your tattoo appointment for a smoother experience.',
    date: '18 Ιουνίου 2026',
  },
  {
    slug: '/blog/pos-epilegeis-tattoo-artist-gia-stil-athina',
    titleEl: 'Πώς Διαλέγεις Tattoo Artist για το Στυλ σου',
    titleEn: 'How to Choose a Tattoo Artist for Your Style',
    descEl: 'Πώς αναγνωρίζεις πραγματική εξειδίκευση σε ένα portfolio, τι να ψάξεις σε κάθε στυλ και πώς κάνεις τη σωστή αντιστοίχιση καλλιτέχνη με σχέδιο στην Αθήνα.',
    descEn: 'How to spot real specialisation in a portfolio, what to look for in each style and how to match the right artist to your design in Athens.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tatouaz-athina-touristes',
    titleEl: 'Τατουάζ στην Αθήνα για Τουρίστες',
    titleEn: 'Getting a Tattoo in Athens as a Tourist',
    descEl: 'Χρόνος επούλωσης, ήλιος και θάλασσα, επιλογή στούντιο, ελληνικό κείμενο και όλα όσα πρέπει να ξέρεις πριν κάνεις τατουάζ στην Αθήνα ως επισκέπτης.',
    descEn: 'Healing time, sun and sea, choosing a studio, Greek lettering and everything you need to know before getting a tattoo in Athens as a visitor.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/lathi-proto-tatouaz-athina',
    titleEl: 'Τα 7 πιο Συχνά Λάθη στο Πρώτο σου Τατουάζ',
    titleEn: 'The 7 Most Common First Tattoo Mistakes',
    descEl: 'Τα 7 πιο συχνά λάθη που κάνουν οι αρχάριοι στο πρώτο τους τατουάζ, γιατί συμβαίνουν και πώς να τα αποφύγεις για το καλύτερο δυνατό αποτέλεσμα.',
    descEn: 'The 7 most common mistakes beginners make with their first tattoo, why they happen and how to avoid them for the best possible result.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-athina-i-peiraias',
    titleEl: 'Tattoo Studio: Αθήνα ή Πειραιάς;',
    titleEn: 'Tattoo Studio: Athens or Piraeus?',
    descEl: 'Τα πλεονεκτήματα του κέντρου της Αθήνας και του Πειραιά, πώς αποφασίζεις σωστά για το δικό σου τατουάζ και τι να ρωτάς σε κάθε στούντιο.',
    descEn: 'The advantages of central Athens and Piraeus, how to decide correctly for your tattoo and what to ask at each studio.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/fotografia-se-tatouaz-athina',
    titleEl: 'Πώς Μετατρέπεις μια Φωτογραφία σε Τατουάζ',
    titleEn: 'How to Turn a Photo into a Tattoo',
    descEl: 'Πώς γίνεται η καλλιτεχνική μετάφραση μιας φωτογραφίας σε τατουάζ, ποιες φωτογραφίες δουλεύουν, τα κατάλληλα στυλ και ρεαλιστικές προσδοκίες για πορτρέτα.',
    descEn: 'How a photo is artistically translated into a tattoo, which photos work, the right styles and realistic expectations for portraits.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/pos-briskeis-idea-tatouaz-athina',
    titleEl: 'Πώς να Βρεις Ιδέα για το Επόμενο Τατουάζ',
    titleEn: 'How to Find an Idea for Your Next Tattoo',
    descEl: 'Πώς ξεκινάς την αναζήτηση ιδέας, πού βρίσκεις έμπνευση πέρα από το ίντερνετ, το τεστ του χρόνου και πρακτικές ασκήσεις για ένα προσωπικό σχέδιο.',
    descEn: 'How to start looking for an idea, where to find inspiration beyond the internet, the test of time and practical exercises for a personal design.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/poia-tatouaz-kaliptontai-cover-up-athina',
    titleEl: 'Ποια Τατουάζ Καλύπτονται πιο Εύκολα με Cover Up',
    titleEn: 'Which Tattoos Are Easier to Cover Up',
    descEl: 'Οι παράγοντες που καθορίζουν την ευκολία ενός cover up, ποια τατουάζ καλύπτονται εύκολα, ποια δυσκολεύουν και ποια σχέδια δουλεύουν καλύτερα.',
    descEn: 'The factors that determine how easy a cover up is, which tattoos are easy to cover, which are harder and which designs work best.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/cover-up-i-laser-athina',
    titleEl: 'Cover Up ή Laser; Τι να Επιλέξεις',
    titleEn: 'Cover Up or Laser? What to Choose',
    descEl: 'Τι σημαίνει cover up και τι laser, οι διαφορές τους σε χρόνο και κόστος, πότε υπερτερεί το καθένα και πώς ο συνδυασμός τους λύνει πολλά προβλήματα.',
    descEn: 'What cover up and laser mean, their differences in time and cost, when each one wins and how combining them solves many problems.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tatouaz-kentro-athinas',
    titleEl: 'Γιατί να Κάνεις Τατουάζ στο Κέντρο της Αθήνας',
    titleEn: 'Why Get a Tattoo in Central Athens',
    descEl: 'Γιατί το κέντρο παραμένει το επίκεντρο της σκηνής tattoo, τι κερδίζεις επιλέγοντάς το και πρακτικές συμβουλές για την επίσκεψή σου.',
    descEn: 'Why the centre remains the heart of the tattoo scene, what you gain by choosing it and practical tips for your visit.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/pio-syxna-lathi-epilogi-sxediou-tatouaz-athina',
    titleEl: 'Τα πιο Συχνά Λάθη στην Επιλογή Σχεδίου Τατουάζ',
    titleEn: 'The Most Common Mistakes When Choosing a Tattoo Design',
    descEl: 'Τα πιο συχνά λάθη στην επιλογή σχεδίου τατουάζ, γιατί συμβαίνουν και πώς να τα αποφύγεις για ένα κομμάτι που θα αγαπάς για μια ζωή.',
    descEn: 'The most common mistakes when choosing a tattoo design, why they happen and how to avoid them for a piece you will love for life.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tatouaz-aresei-se-10-xronia-athina',
    titleEl: 'Θα σου Αρέσει το Τατουάζ και σε 10 Χρόνια;',
    titleEn: 'Will You Still Like Your Tattoo in 10 Years?',
    descEl: 'Πώς αξιολογείς ένα σχέδιο τατουάζ για τη διαχρονικότητά του, ποιοι παράγοντες το κάνουν να αντέχει και πώς παίρνεις μια απόφαση χωρίς μετάνοιες.',
    descEn: 'How to evaluate a tattoo design for timelessness, what makes it last and how to make a decision without regrets.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/pos-katalabaineis-tatouaz-molynsi-athina',
    titleEl: 'Πώς Καταλαβαίνεις ότι ένα Τατουάζ έχει Μολυνθεί',
    titleEn: 'How to Tell If a Tattoo Is Infected',
    descEl: 'Πώς ξεχωρίζεις τη φυσιολογική επούλωση από τη μόλυνση, ποια σημάδια σε βάζουν σε επιφυλακή, πότε να δεις γιατρό και πώς προλαμβάνεις τη μόλυνση.',
    descEn: 'How to tell normal healing from infection, which signs to watch for, when to see a doctor and how to prevent infection.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/boro-tatouaz-an-eimai-arrostos-athina',
    titleEl: 'Μπορώ να Κάνω Τατουάζ αν Είμαι Άρρωστος;',
    titleEn: 'Can I Get a Tattoo If I Am Sick?',
    descEl: 'Γιατί δεν πρέπει να κάνεις τατουάζ όταν είσαι άρρωστος, ποιες καταστάσεις χρειάζονται αναβολή, τι να κάνεις και πώς να προγραμματίσεις σωστά.',
    descEn: 'Why you should not get a tattoo when sick, which conditions require postponement, what to do and how to plan correctly.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/boro-gymnastiko-meta-tatouaz-athina',
    titleEl: 'Μπορώ να Γυμναστώ μετά από Τατουάζ;',
    titleEn: 'Can I Work Out After a Tattoo?',
    descEl: 'Πόσο πρέπει να περιμένεις πριν το γυμναστήριο μετά από τατουάζ, οι κίνδυνοι ανά τύπο άσκησης και πώς να επιστρέψεις σταδιακά στη ρουτίνα σου.',
    descEn: 'How long to wait before the gym after a tattoo, the risks per type of exercise and how to return gradually to your routine.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tatouaz-xefloudizei-fagourizei-athina',
    titleEl: 'Τατουάζ που Ξεφλουδίζει και Φαγουρίζει: Φυσιολογικό;',
    titleEn: 'Tattoo Peeling and Itching: Is It Normal?',
    descEl: 'Γιατί ξεφλουδίζει και φαγουρίζει ένα φρέσκο τατουάζ, τι είναι φυσιολογικό, πώς διαχειρίζεσαι τη φαγούρα σωστά και πότε πρέπει να ανησυχήσεις.',
    descEn: 'Why a fresh tattoo peels and itches, what is normal, how to manage the itch correctly and when to worry.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tatouaz-pleura-poso-ponaei-athina',
    titleEl: 'Τατουάζ στα Πλευρά: Πόσο Πονάει;',
    titleEn: 'Rib Tattoos: How Much Do They Hurt?',
    descEl: 'Γιατί το τατουάζ στα πλευρά είναι από τα πιο επώδυνα, πόσο πονάει πραγματικά, τι να περιμένεις και πώς να προετοιμαστείς σωστά.',
    descEn: 'Why rib tattoos are among the most painful, how much they really hurt, what to expect and how to prepare correctly.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/pio-epodyna-simeia-tatouaz-athina',
    titleEl: 'Τα πιο Επώδυνα Σημεία για Τατουάζ',
    titleEn: 'The Most Painful Spots for a Tattoo',
    descEl: 'Ποια είναι τα πιο επώδυνα σημεία για τατουάζ, γιατί πονούν τόσο και πώς να διαχειριστείς την εμπειρία ώστε να τη ζήσεις όσο πιο ήρεμα γίνεται.',
    descEn: 'Which are the most painful spots for a tattoo, why they hurt so much and how to manage the experience as calmly as possible.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/ligotero-epodyna-simeia-tatouaz-athina',
    titleEl: 'Τα Λιγότερο Επώδυνα Σημεία για Τατουάζ',
    titleEn: 'The Least Painful Spots for a Tattoo',
    descEl: 'Ποια είναι τα λιγότερο επώδυνα σημεία για τατουάζ, γιατί πονούν λιγότερο και πώς να διαλέξεις το ιδανικό σημείο για το πρώτο σου κομμάτι.',
    descEn: 'Which are the least painful spots for a tattoo, why they hurt less and how to choose the ideal spot for your first piece.',
    date: '7 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-exarchia',
    titleEl: 'Τατουάζ κοντά στα Εξάρχεια',
    titleEn: 'Tattoo Studio near Exarchia',
    descEl: 'Το 210 Tattoo Athens είναι λίγα λεπτά με τα πόδια από την πλατεία Εξαρχείων. Custom, fine line και blackwork τατουάζ στη γειτονική Γκύζη.',
    descEn: 'The studio is a few minutes on foot from Exarchia square. Custom, fine line and blackwork tattoos in neighbouring Gyzi.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-ambelokipi',
    titleEl: 'Τατουάζ κοντά στους Αμπελόκηπους',
    titleEn: 'Tattoo Studio near Ambelokipi',
    descEl: 'Λίγα λεπτά από το Πανόρμου: fine line, lettering, cover up και custom τατουάζ, ιδανικά για ραντεβού μετά τη δουλειά.',
    descEn: 'A few minutes from Panormou: fine line, lettering, cover up and custom tattoos, ideal for an after-work appointment.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-kypseli',
    titleEl: 'Τατουάζ κοντά στην Κυψέλη',
    titleEn: 'Tattoo Studio near Kypseli',
    descEl: 'Η Κυψέλη είναι ουσιαστικά η διπλανή γειτονιά του studio. Έγχρωμα, old school, black & gray και custom τατουάζ σε απόσταση βόλτας.',
    descEn: 'Kypseli is practically the next-door neighbourhood of the studio. Color, old school, black & gray and custom tattoos a short walk away.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-neapoli-exarchia',
    titleEl: 'Τατουάζ στη Νεάπολη Εξαρχείων',
    titleEn: 'Tattoo Studio near Neapoli',
    descEl: 'Από τη Νεάπολη το studio είναι 5-8 λεπτά με τα πόδια. Fine line, realistic, sleeve και custom τατουάζ, κυριολεκτικά δίπλα.',
    descEn: 'From Neapoli the studio is a 5-8 minute walk. Fine line, realistic, sleeve and custom tattoos, literally next door.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-kolonaki',
    titleEl: 'Τατουάζ κοντά στο Κολωνάκι',
    titleEn: 'Tattoo Studio near Kolonaki',
    descEl: 'Η Γκύζη συνορεύει με το Κολωνάκι μέσω του Λυκαβηττού. Fine line, lettering και custom τατουάζ, λίγα λεπτά από την πλατεία Δεξαμενής.',
    descEn: 'Gyzi borders Kolonaki via Lycabettus. Fine line, lettering and custom tattoos a few minutes from Dexameni square.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-pagkrati',
    titleEl: 'Τατουάζ κοντά στο Παγκράτι',
    titleEn: 'Tattoo Studio near Pagkrati',
    descEl: 'Blackwork, dotwork, sleeve και custom τατουάζ για το δημιουργικό κοινό του Παγκρατίου, 15 λεπτά με λεωφορείο ή ταξί.',
    descEn: 'Blackwork, dotwork, sleeve and custom tattoos for the creative crowd of Pagkrati, 15 minutes by bus or taxi.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-syntagma-monastiraki',
    titleEl: 'Τατουάζ κοντά στο Σύνταγμα και την Πλάκα',
    titleEn: 'Tattoo Studio near Syntagma & Plaka',
    descEl: 'Λίγα λεπτά από το ιστορικό κέντρο: ποιοτικό τατουάζ με consultation, μακριά από τη φασαρία, ιδανικό και για επισκέπτες.',
    descEn: 'Minutes from the historic centre: quality tattoos with a proper consultation, away from the crowds, great for visitors too.',
    date: '17 Ιουλίου 2026',
  },
  {
    slug: '/blog/tattoo-studio-near-petralona',
    titleEl: 'Τατουάζ κοντά στα Πετράλωνα',
    titleEn: 'Tattoo Studio near Petralona',
    descEl: 'Λίγες στάσεις μετρό από τα Πετράλωνα: ρεαλιστικά, black & gray και cover up τατουάζ με πραγματική εξειδίκευση.',
    descEn: 'A few metro stops from Petralona: realistic, black & gray and cover up tattoos with real specialisation.',
    date: '17 Ιουλίου 2026',
  },
];

const blogIndexSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Blog | 210 Tattoo Athens",
  "description": "Άρθρα για τατουάζ στην Αθήνα: οδηγίες φροντίδας, στυλ, τάσεις και συμβουλές από το 210 Tattoo Athens.",
  "url": "https://210tattoo.gr/blog",
  "publisher": {
    "@type": "Organization",
    "name": "210 Tattoo Athens",
    "url": "https://210tattoo.gr",
    "logo": { "@type": "ImageObject", "url": "https://210tattoo.gr/og-image.png" }
  },
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": posts.map((p, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "url": `https://210tattoo.gr${p.slug}`,
      "name": p.titleEl,
    })),
  },
};

export default function BlogIndex() {
  const { t, i18n } = useTranslation();
  const { openManager } = useConsent();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isEl = i18n.language === 'el';

  return (
    <div className="text-white min-h-screen flex flex-col bg-black">
      <Helmet>
        <title>Blog | Τατουάζ Αθήνα | 210 Tattoo Athens</title>
        <meta name="description" content="Άρθρα για τατουάζ στην Αθήνα: οδηγίες φροντίδας, στυλ, τάσεις και συμβουλές για το πρώτο σου τατουάζ από το 210 Tattoo Athens." />
        <link rel="canonical" href="https://210tattoo.gr/blog" />
        <link rel="alternate" hrefLang="el" href="https://210tattoo.gr/blog" />
        <link rel="alternate" hrefLang="x-default" href="https://210tattoo.gr/blog" />
        <meta property="og:title" content="Blog | Τατουάζ Αθήνα | 210 Tattoo Athens" />
        <meta property="og:description" content="Άρθρα για τατουάζ στην Αθήνα: οδηγίες φροντίδας, στυλ, τάσεις και συμβουλές." />
        <meta property="og:url" content="https://210tattoo.gr/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage('blog')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog | 210 Tattoo Athens" />
        <meta name="twitter:description" content="Άρθρα για τατουάζ στην Αθήνα: οδηγίες φροντίδας, στυλ και τάσεις." />
        <meta name="twitter:image" content={ogImage('blog')} />
        <script type="application/ld+json">{JSON.stringify(blogIndexSchema)}</script>
      </Helmet>

      {/* Nav */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b border-pink-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="flex items-center gap-3">
                <picture>
                  <source srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} type="image/jpeg" />
                  <img src={logoImage80} srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} alt="210 Tattoo Studio" className="h-20 w-auto rounded-lg object-contain" width={80} height={80} loading="eager" decoding="async" />
                </picture>
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-8 ml-10">
              <a href="/#home" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.home')}</a>
              <div className="relative group">
                <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.studioMenu')}</button>
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-48 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                  <a href="/#about" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.about')}</a>
                  <a href="/#services" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.services')}</a>
                </div>
              </div>
              <a href="/#gallery" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.gallery')}</a>
              <Link to="/blog" className="px-3 py-2 text-sm font-medium text-pink-400 border-b-2 border-pink-500">{t('nav.blog')}</Link>
              <div className="relative group">
                <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.helpMenu')}</button>
                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-56 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                  <a href="/#faq" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.faq')}</a>
                  <a href="/#contact" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.contact')}</a>
                </div>
              </div>
              <a href="/spin" className="px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-600 to-pink-700 rounded-lg hover:from-pink-700 hover:to-pink-800 shadow-lg">{t('nav.spin')}</a>
              <div className="flex items-center gap-2 pl-4 border-l border-pink-600/20">
                <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to Greek">ΕΛ</button>
                <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to English">EN</button>
              </div>
              <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg">
                <Instagram size={16} />{t('nav.dmCta')}
              </a>
              <AuthMenu />
            </div>
            <div className="md:hidden flex items-center gap-2">
              <AuthMenu />
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-300 hover:text-white p-2" aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={isMenuOpen} aria-controls="mobile-menu">
                {isMenuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
              </button>
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-black border-t border-pink-600/20">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {(['home','about','services','gallery','faq','contact'] as const).map((id) => (
                <a key={id} href={`/#${id}`} className="block px-3 py-2 text-base font-medium text-gray-300 hover:text-pink-400">{t(`nav.${id}` as any)}</a>
              ))}
              <Link to="/blog" className="block px-3 py-2 text-base font-medium text-pink-400">{t('nav.blog')}</Link>
              <a href="/spin" className="block px-3 py-2 text-base font-medium text-pink-300 hover:text-pink-400">{t('nav.spin')}</a>
              <div className="flex items-center gap-2 mt-2 px-3">
                <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>ΕΛ</button>
                <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>EN</button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-28 flex-1">
        {/* Hero */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-950/20 via-black to-black pointer-events-none" />
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center gap-2 text-pink-500 text-sm font-medium mb-6">
              <a href="/" className="hover:text-pink-400 transition-colors">Home</a>
              <ChevronRight size={14} />
              <span>Blog</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-pink-500 via-pink-400 to-pink-300 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl">
              {isEl
                ? 'Άρθρα για τατουάζ στην Αθήνα: οδηγίες φροντίδας, στυλ, τάσεις και συμβουλές από το 210 Tattoo Athens.'
                : 'Articles about tattoos in Athens: aftercare guides, styles, trends and tips from 210 Tattoo Athens.'}
            </p>
          </div>
        </section>

        {/* Posts grid */}
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-6">
              {posts.map((post) => (
                <Link
                  key={post.slug}
                  to={post.slug}
                  className="group bg-gray-900 rounded-xl p-7 border border-pink-600/10 hover:border-pink-600/40 transition-all duration-200 flex flex-col sm:flex-row sm:items-start gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-pink-600/60 text-xs mb-2">
                      <CalendarDays size={13} />
                      <span>{post.date}</span>
                    </div>
                    <h2 className="text-xl font-bold text-white group-hover:text-pink-400 transition-colors mb-2">
                      {isEl ? post.titleEl : post.titleEn}
                    </h2>
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
                      {isEl ? post.descEl : post.descEn}
                    </p>
                  </div>
                  <div className="shrink-0 flex items-center self-end sm:self-center text-pink-500 group-hover:translate-x-1 transition-transform duration-200">
                    <ChevronRight size={22} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 border-t border-pink-600/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              {isEl ? 'Έτοιμοι για το επόμενο τατουάζ σας;' : 'Ready for your next tattoo?'}
            </h2>
            <p className="text-gray-400 mb-8">
              {isEl ? 'Στείλτε μας DM στο Instagram για ραντεβού.' : 'Send us a DM on Instagram to book an appointment.'}
            </p>
            <a
              href="https://www.instagram.com/210tattoo/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 inline-flex items-center gap-3 shadow-lg hover:shadow-pink-500/25"
            >
              <Instagram size={20} />
              {isEl ? 'Κλείστε Ραντεβού' : 'Book Appointment'}
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-pink-500 mb-4">{t('footer.studio')}</h3>
          <p className="text-gray-400 mb-6">{t('footer.tagline')}</p>
          <div className="flex justify-center mb-8">
            <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors" aria-label={t('footer.instagramLabel')}>
              <Instagram size={24} aria-hidden />
            </a>
          </div>
          <nav className="mb-8">
            <ul className="flex justify-center gap-6 text-sm flex-wrap">
              <li><Link to="/privacy" className="text-gray-400 hover:text-pink-400">{t('footer.privacy')}</Link></li>
              <li><Link to="/cookies" className="text-gray-400 hover:text-pink-400">{t('footer.cookies')}</Link></li>
              <li><Link to="/terms" className="text-gray-400 hover:text-pink-400">{t('footer.terms')}</Link></li>
              <li><button type="button" onClick={openManager} className="text-gray-400 hover:text-pink-400">{t('consent.manage')}</button></li>
            </ul>
          </nav>
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-300 text-sm">{t('footer.rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
